import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRiskStore } from '@/stores/riskData';
import { SavePaymentPreference } from '@/composables/useQuoteAPIRequests';

// Annual vs monthly view — shared module-level so toggling re-prices every card at once.
const isMonthly = ref(typeof window !== 'undefined' && (window as any).isMonthly === true);

// Toggle + persist the choice server-side, so any later reload restores it.
const setMonthly = (value: boolean) => {
    if (isMonthly.value === value) return;
    isMonthly.value = value;
    SavePaymentPreference(value).catch(() => { /* best-effort; also posted on Buy now */ });
};

// Shared quote-summary derivations for the single-scheme display and its docked summary bar, read from the store.
export function useSingleSchemeSummary(quoteId?: string) {
    const store = useRiskStore();
    const { productOutput, action } = storeToRefs(store);

    // MTA shows the pro-rata adjustment (ProRataPremium/ProRataPaymentPlan), not the annual premium.
    const isMta = computed(() => /mta/i.test(String(action.value ?? '')));

    const scheme = computed(() => {
        const schemes = productOutput.value?.ReturnSchemes ?? [];
        return schemes.find((s: any) => s.IsSelected) ?? schemes[0] ?? null;
    });

    const price = (n: any) => `£${(Number(n) || 0).toFixed(2)}`;

    const coverPremium = (c: any) => (isMta.value ? c?.ProRataPremium : c?.QuotedPremium);
    const schemePremium = computed(() => (isMta.value ? scheme.value?.ProRataPremium : scheme.value?.QuotedPremium));
    const effectiveDate = computed(() => productOutput.value?.EffectiveDate ?? scheme.value?.EffectiveDate ?? null);

    // Only genuinely-removed covers belong in the MTA breakdown — they carry a return (negative pro-rata)
    // or IsRefunded. A deselected cover the server speculatively pro-rates as an *addition* (positive) is not shown.
    const mtaRemoved = (c: any) => isMta.value && (c?.IsRefunded === true || (Number(c?.ProRataPremium?.TotalPayable) || 0) < 0);
    const selectedCovers = computed(() =>
        (scheme.value?.Covers ?? []).filter((c: any) => c.IsMandatory || c.IsSelected || mtaRemoved(c)));
    // New business: sum of selected covers' annual premiums. MTA: the scheme-level pro-rata total.
    const total = computed(() => isMta.value
        ? Number(schemePremium.value?.TotalPayable) || 0
        : selectedCovers.value.reduce((sum: number, c: any) => sum + (Number(c?.QuotedPremium?.TotalPayable) || 0), 0));

    // MTA per-cover monthly display
    const MTA_MONTHLY_PER_COVER = true;
    const coverMonthly = computed(() => isMonthly.value && (!isMta.value || MTA_MONTHLY_PER_COVER));

    // A cover's monthly figure = its share of the monthly instalment
    const monthlyFactor = computed(() => {
        if (isMta.value) {
            const n = Number(scheme.value?.ProRataPaymentPlan?.NumberOfInstallments) || 0;
            return n > 0 ? 1 / n : 0;
        }
        const t = Number(schemePremium.value?.TotalPayable) || 0;
        const m = scheme.value?.PaymentPlan?.InstallmentAmount;
        return (t > 0 && m != null) ? Number(m) / t : 0;
    });
    // Monthly-aware price for a cover's annual (or MTA pro-rata) amount — used by the cards + summary breakdown.
    const priceDisplay = (annualValue: any) => {
        const a = Number(annualValue) || 0;
        return price(coverMonthly.value && monthlyFactor.value ? a * monthlyFactor.value : a);
    };
    // MTA cover premiums are signed like COH: '+' for an added cost, '-' for a refund.
    const priceDisplaySigned = (value: any) => {
        const n = Number(value) || 0;
        if (!isMta.value) return priceDisplay(n);
        return (n < 0 ? '-' : '+') + priceDisplay(Math.abs(n));
    };

    const monthly = computed(() => {
        if (isMta.value) {
            const pp = scheme.value?.ProRataPaymentPlan;
            if (!pp || pp.NextCollectionAmount == null) return null;
            return {
                amount: pp.NextCollectionAmount,
                sub: Number(pp.NumberOfInstallments) > 0 ? `${pp.NumberOfInstallments} collections remaining` : 'incl. IPT',
            };
        }
        const pp = scheme.value?.PaymentPlan;
        if (!pp || pp.InstallmentAmount == null) return null;
        return {
            amount: pp.InstallmentAmount,
            sub: [pp.DepositAmount != null ? `${price(pp.DepositAmount)} deposit` : null,
                  pp.NumberOfInstallments != null ? `then ${pp.NumberOfInstallments} installments of ${price(pp.InstallmentAmount)}` : null].filter(Boolean).join(', ') || 'incl. IPT',
        };
    });

    // Payment-plan figures for the MD-style monthly breakdown (deposit / instalments / total).
    const plan = computed(() => {
        if (isMta.value) {
            const pp = scheme.value?.ProRataPaymentPlan;
            if (!pp || pp.NextCollectionAmount == null) return null;
            return {
                deposit: 0,
                instalment: Number(pp.NextCollectionAmount) || 0,
                prevInstalment: Number(pp.PreviousInstallmentAmount) || 0,
                installments: Number(pp.NumberOfInstallments) || 0,
                total: Number(pp.TotalPayable ?? pp.PremiumTotal) || 0,
                apr: 0,
            };
        }
        const pp = scheme.value?.PaymentPlan;
        if (!pp || pp.InstallmentAmount == null) return null;
        return {
            deposit: Number(pp.DepositAmount) || 0,
            instalment: Number(pp.InstallmentAmount) || 0,
            installments: Number(pp.NumberOfInstallments) || 0,
            total: Number(pp.TotalPayable ?? pp.PremiumTotal) || 0,
            apr: pp.APR != null ? Number(pp.APR) : 0,
        };
    });

    // IPT % (e.g. 12) and monthly APR — for the "prices include IPT" note under the premiums.
    const iptRate = computed(() => schemePremium.value?.IptRate ?? scheme.value?.QuotedPremium?.IptRate ?? null);
    const iptNote = computed(() => {
        const rate = iptRate.value;
        const lead = monthly.value ? 'Both prices include IPT' : 'Price includes IPT';
        let note = rate != null ? `${lead} at ${Number(rate).toFixed(1)}%.` : `${lead}.`;
        const pp = scheme.value?.PaymentPlan;
        if (!isMta.value && monthly.value && pp?.NumberOfInstallments) {
            note += ` Monthly is spread over ${pp.NumberOfInstallments} months`;
            note += pp.APR != null ? ` at ${Number(pp.APR).toFixed(0)}% APR.` : '.';
        }
        return note;
    });

    // Prefer the rated reference, else the reference the Razor page renders in its title.
    const domRef = () => (typeof document !== 'undefined' ? document.getElementById('QuoteReference')?.textContent?.trim() : '') || '';
    const quoteRef = computed(() =>
        productOutput.value?.QuoteReference || scheme.value?.QuoteReference || domRef() || quoteId || '—');

    // ProductOutput.Documents is List<ProductDocument> — Key = name, Value = link.
    const documents = computed<Array<{ name: string; link: string }>>(() =>
        (productOutput.value?.Documents ?? [])
            .map((d: any) => ({ name: d?.Key ?? d?.Name, link: d?.Value ?? d?.Link }))
            .filter((d: any) => d.name));
    // Display name without the file extension (names arrive as e.g. "Policy Wording.pdf").
    const docName = (n: string) => (n || '').replace(/\.[a-z0-9]{2,4}$/i, '');

    return { scheme, price, priceDisplay, priceDisplaySigned, isMonthly, coverMonthly, setMonthly, isMta, coverPremium, effectiveDate, selectedCovers, total, monthly, plan, iptRate, iptNote, quoteRef, documents, docName };
}
